import React from "react";
import Popup from "reactjs-popup";
import { BolFieldValue } from "../../../../app/data/pickup-requests/bolModels";
import { fieldHasDisplayValue, fieldNeedsAttention } from "../../../../app/data/pickup-requests/bolModels";
import { shouldShowAiBadge, shouldShowMissingBadge } from "../utils/bolFieldBadges";

export interface BolFieldInputProps {
  label: string;
  field?: BolFieldValue | null;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  type?: "text" | "number";
  showAiBadge?: boolean;
  compact?: boolean;
  variant?: "default" | "figma";
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
}

const MissingBadge: React.FC<{ aiMessage?: string | null }> = ({ aiMessage }) => {
  const badge = (
    <span className="xgs-bol-field__badge xgs-bol-field__badge--missing xgs-bol-field__badge--missing-tooltip">
      Missing
    </span>
  );

  if (!aiMessage) return badge;

  return (
    <Popup
      trigger={badge}
      position="top center"
      on={["hover"]}
      offsetY={5}
      arrowStyle={{ borderWidth: "0px" }}
      contentStyle={{
        borderRadius: "4px",
        boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.20)",
        borderWidth: "0px",
        padding: "8px 10px",
        fontSize: "12px",
        lineHeight: "1.4",
        maxWidth: "280px",
      }}
    >
      <div className="xgs-bol-field__missing-tooltip">{aiMessage}</div>
    </Popup>
  );
};

const BolFieldInput: React.FC<BolFieldInputProps> = ({
  label,
  field,
  readOnly,
  onChange,
  type = "text",
  showAiBadge = true,
  compact = false,
  variant = "default",
  placeholder,
  required = false,
  multiline = false,
}) => {
  const hasValue = fieldHasDisplayValue(field);
  const isMissing = !hasValue;
  const needsAttention = fieldNeedsAttention(field);
  const showAi = shouldShowAiBadge(field, showAiBadge);
  const showMissing = shouldShowMissingBadge(field, readOnly);
  const showRequiredError = required && isMissing && !readOnly;
  const isFigma = variant === "figma";

  const labelRow = label ? (
    <div className="xgs-bol-field__label-row">
      <span className={`xgs-bol-field__label${isFigma ? " xgs-bol-field__label--figma" : ""}`}>
        {label}
        {required && <span className="xgs-bol-field__required"> *</span>}
      </span>
      {showAi && <span className="xgs-bol-field__badge xgs-bol-field__badge--ai">AI</span>}
      {showMissing && <MissingBadge aiMessage={field?.aiMessage} />}
    </div>
  ) : null;

  const baseInputClass = isFigma ? "xgs-bol-field__figma-input" : "xgs-bol-field__inline-input";
  const inputClass = [
    baseInputClass,
    multiline ? "xgs-bol-field__figma-textarea" : "",
    showRequiredError ? `${baseInputClass}--error` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rootClass = [
    "xgs-bol-field",
    compact ? "xgs-bol-field--compact" : "",
    hasValue && needsAttention ? "xgs-bol-field--warn" : "",
    isFigma ? "xgs-bol-field--figma" : "",
    multiline ? "xgs-bol-field--multiline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const resolvedPlaceholder =
    placeholder || (label ? (isFigma && !required ? "Optional" : `Enter ${label.toLowerCase()}`) : "");

  if (!field && !readOnly && onChange) {
    return (
      <div className={rootClass}>
        {labelRow}
        {multiline ? (
          <textarea
            className={inputClass}
            value=""
            placeholder={resolvedPlaceholder}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            className={inputClass}
            type={type}
            value=""
            placeholder={resolvedPlaceholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    );
  }

  if (!hasValue && field) {
    return (
      <div className={rootClass}>
        {labelRow}
        {!readOnly && onChange &&
          (multiline ? (
            <textarea
              className={inputClass}
              placeholder={resolvedPlaceholder}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            <input
              className={inputClass}
              type={type}
              placeholder={resolvedPlaceholder}
              onChange={(e) => onChange(e.target.value)}
            />
          ))}
        {readOnly && <div className="xgs-bol-field__value">—</div>}
      </div>
    );
  }

  const displayValue = field?.value != null ? String(field.value) : "";

  if (readOnly || !onChange) {
    return (
      <div className={rootClass}>
        {labelRow}
        {multiline ? (
          <div className="xgs-bol-field__value xgs-bol-field__value--multiline">{displayValue || "—"}</div>
        ) : (
          <div className={isFigma ? "xgs-bol-field__figma-readonly" : "xgs-bol-field__value"}>
            {displayValue || "—"}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {labelRow}
      {multiline ? (
        <textarea
          className={inputClass}
          value={displayValue}
          placeholder={resolvedPlaceholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          type={type}
          value={displayValue}
          placeholder={resolvedPlaceholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
};

export default BolFieldInput;
